import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AuthService } from '@shared/services/auth/auth.service';
import { UploadInput } from 'ngx-uploader';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorResponse } from '../../response';

@Injectable()
export class FileService {
    constructor(
        private readonly authService: AuthService,
        private readonly http: HttpClient
    ) { }

    private static get URL(): string { return `${environment.dashboardApi.baseUrl}${environment.dashboardApi.prefix}`; }
    private static readonly ENDPOINT = 'files';

    public createNgxUploadEvent(): UploadInput {
        const token = this.authService.retrieveToken();

        const headers = {};
        if (token) {
            headers['Authorization'] = 'Bearer ' + token.encoded;
        }

        const url = FileService.URL + FileService.ENDPOINT + '/upload';

        return {
            type: 'uploadAll',
            url,
            method: 'POST',
            headers: {
                ...headers
            }
        };
    }

    public downloadFile(blobName: string) {
        const url = FileService.URL + FileService.ENDPOINT + '/download/' + blobName;

        // Method for downloading files with auth token: https://stackoverflow.com/questions/54753021/how-can-i-pass-an-auth-token-when-downloading-a-file
        this.http
            .get(url, {
                responseType: 'blob',
                observe: 'response'
            }).pipe(
                catchError(x => of(new ErrorResponse(x?.error?.messages))))
            .subscribe((response: HttpResponse<Blob>) => {
                const urlObject = URL.createObjectURL(response.body);

                // Create an anchor element to "point" to it
                const anchor = document.createElement('a');
                anchor.href = urlObject;

                // Get the suggested filename for the file from the response headers
                anchor.download = this.getFilenameFromHeaders(response.headers) || 'file';

                // Simulate a click on our anchor element
                anchor.click();

                // Discard the object data
                URL.revokeObjectURL(urlObject);
            });
    }

    // From https://stackoverflow.com/questions/54753021/how-can-i-pass-an-auth-token-when-downloading-a-file
    private getFilenameFromHeaders(headers: HttpHeaders) {
        // The content-disposition header should include a suggested filename for the file
        const contentDisposition = headers.get('Content-Disposition');
        if (!contentDisposition) {
            return null;
        }

        /* StackOverflow is full of RegEx-es for parsing the content-disposition header,
        * but that's overkill for my purposes, since I have a known back-end with
        * predictable behaviour. I can afford to assume that the content-disposition
        * header looks like the example in the docs
        * https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Disposition
        *
        * In other words, it'll be something like this:
        *    Content-Disposition: attachment; filename="filename.ext"
        *
        * I probably should allow for single and double quotes (or no quotes) around
        * the filename. I don't need to worry about character-encoding since all of
        * the filenames I generate on the server side should be vanilla ASCII.
        */

        const leadIn = 'filename=';
        const start = contentDisposition.search(leadIn);
        if (start < 0) {
            return null;
        }

        // Get the 'value' after the filename= part (which may be enclosed in quotes)
        const value = contentDisposition.substring(start + leadIn.length).trim();
        if (value.length === 0) {
            return null;
        }

        // If it's not quoted, we can return the whole thing until we encounter a ";"
        const firstCharacter = value[0];
        if (firstCharacter !== '\"' && firstCharacter !== '\'') {
            const semicolon = value.indexOf(';');
            if (semicolon !== -1) {
                return value.substring(0, semicolon);
            }
            return value;
        }

        // If it's quoted, it must have a matching end-quote
        if (value.length < 2) {
            return null;
        }

        // The end-quote must match the opening quote
        const lastCharacter = value[value.length - 1];
        if (lastCharacter !== firstCharacter) {
            return null;
        }

        // Return the content of the quotes
        return value.substring(1, value.length - 1);
    }
}
